import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Grid, Paper, Typography, Tooltip, IconButton } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import Filter from 'components/Filter/components';
import FilterMobile from 'components/FilterMobile/components';
import MTable from 'components/MTable';
import TableMobile from 'components/TableMobile';
import Button from 'components/Button';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faPen } from '@fortawesome/free-solid-svg-icons';

import { loadSupplierList, loadSupplierExport } from '../store/thunk';
import { clearSupplier } from '../../SupplierRegistration/store/actions';

import { inputs } from './resources';

import useDidMount from 'helpers/hooks/useDidMount';
import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

const headers = [
  { title: 'Responsável', field: 'name' },
  { title: 'Nome', field: 'fantasyName' },
  { title: 'CNPJ', field: 'cnpj' },
  { title: 'Telefone', field: 'phone' }
];

const exportHeaders = [
  { id: 'name', label: 'Responsável' },
  { id: 'fantasyName', label: 'Nome' },
  { id: 'cnpj', label: 'CNPJ' },
  { id: 'phone', label: 'Telefone' },
  { id: 'address', label: 'Endereço' }
];

export default function SupplierList() {
  const dispatch = useDispatch();

  const history = useHistory();

  const didComponentMount = useDidMount();

  const { width } = useWindowDimensions();
  const isMobile = width < 1024;

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { isLoading, total, list } = useSelector(state => state.supplierList);
  const { qp } = useSelector(state => state.filters);

  const [supplierPage, setSupplierPage] = useState(1);
  const supplierPageSize = 10;
  const supplierPageCount = Math.ceil(total / supplierPageSize);

  const handleLoadSupplierExport = () => {
    return dispatch(loadSupplierExport(userId, establishmentId, qp));
  }

  const requestSupplierList = ({ establishmentId, page, qp }) => {
    dispatch(loadSupplierList(userId, establishmentId, page, qp));
  }

  useEffect(() => {
    if(didComponentMount) {
      requestSupplierList({ establishmentId, page: supplierPage, qp });
    }
  }, []);

  useEffect(() => {
    if(establishmentId && !didComponentMount) {
      requestSupplierList({ establishmentId, page: supplierPage, qp });
    }
  }, [establishmentId]);

  useEffect(() => {
    if(!didComponentMount) {
      requestSupplierList({ establishmentId, page: supplierPage, qp });
    }
  }, [supplierPage]);

  return(
    <Paper>
      {isMobile ? (
        <FilterMobile
          alternativeExport
          functionAlternativeExport={handleLoadSupplierExport}
          exportType='supplier'
          exportHeaders={exportHeaders}
          filter
          filters={inputs}
          handlePageRequest={({ qp }) => {
            setSupplierPage(1);
            return requestSupplierList({ establishmentId, page: 1, qp });
          }}
          actionButton={
            <Button
              fullWidth
              color="success"
              startIcon={
                <FontAwesomeIcon
                  icon={faPlusCircle}
                  color="#FFFFFF"
                  size="xs"
                />
              }
              onClick={() => {
                dispatch(clearSupplier());
                history.push('/novo-fornecedor');
              }}
            >
              Adicionar Fornecedor
            </Button>
          }
        />
      ) : (
        <Filter
          alternativeExport
          functionAlternativeExport={handleLoadSupplierExport}
          exportType='supplier'
          exportHeaders={exportHeaders}
          filter
          filters={inputs}
          handlePageRequest={({ qp }) => {
            setSupplierPage(1);
            return requestSupplierList({ establishmentId, page: 1, qp });
          }}
          actionButton={
            <Button
              fullWidth
              color="success"
              startIcon={
                <FontAwesomeIcon
                  icon={faPlusCircle}
                  color="#FFFFFF"
                  size="xs"
                />
              }
              onClick={() => {
                dispatch(clearSupplier());
                history.push('/novo-fornecedor');
              }}
            >
              Adicionar Fornecedor
            </Button>
          }
        />
      )}
      {isMobile && (
        <TableMobile
          loading={isLoading}
          data={list?.map(item => {
            return {
              ...item,
              name: {
                index: 1,
                label: 'Responsável',
                value: <Typography color='textSecondary' variant='subtitle2'>{item?.name}</Typography>
              },
              corporateName: {
                index: 2,
                label: 'Nome',
                value: <Typography color='textSecondary' variant='subtitle2'>{item?.fantasyName}</Typography>
              },
              cnpj: {
                index: 3,
                label: 'CNPJ',
                value: <Typography color='textSecondary' variant='subtitle2'>{item?.cnpj}</Typography>
              },
              phone: {
                index: 4,
                label: 'Telefone',
                value: <Typography color='textSecondary' variant='subtitle2'>{item?.phone}</Typography>
              }
            }
          })}
          actions={[
            {
              action: rowData => (
                <Tooltip placement='top' title='Editar Fornecedor' enterTouchDelay={0}>
                  <IconButton onClick={() => {
                    const { uuid } = rowData;

                    return history.push({
                      pathname: '/editar-fornecedor',
                      state: {
                        data: {
                          uuid
                        }
                      }
                    });
                  }}>
                    <FontAwesomeIcon
                      icon={faPen}
                      color="#022A5C"
                      size="xs"
                    />
                  </IconButton>
                </Tooltip>
              )
            }
          ]}
        />
      )}
      {!isMobile && (
        <MTable
          loading={isLoading}
          headers={headers}
          data={list}
          actions={[
            rowData => ({
              icon: () => (
                <FontAwesomeIcon
                  icon={faPen}
                  color="#022A5C"
                  size="xs"
                />
              ),
              tooltip: 'Editar Fornecedor',
              onClick: (_, rowData) => {
                const { uuid } = rowData;

                return history.push({
                  pathname: '/editar-fornecedor',
                  state: {
                    data: {
                      uuid
                    }
                  }
                });
              }
            })
          ]}
        />
      )}
      <Grid
        container
        xs={12}
        style={{ padding: 10, display: 'flex', justifyContent: 'flex-end' }}
      >
        <Pagination
          color="primary"
          variant="outlined"
          shape="rounded"
          count={supplierPageCount}
          page={supplierPage}
          onChange={(_, page) => setSupplierPage(page)}
        />
      </Grid>
    </Paper>
  );
}